import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, combineLatest } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import * as fromAuth from '../../../authentication/store/selectors/authentication.selector';
import { EarlyAccessFeatureDialogComponent } from '../../../portfolio-manager/components/dialogs/early-access-feature-dialog/early-access-feature-dialog.component';
import { TokenSwapperComponent } from '../../../portfolio-manager/components/token-swapper/token-swapper.component';
import { MarketFeed } from '../../../portfolio-manager/models/market.model';
import { TickerFeed } from '../../../portfolio-manager/models/ticker.model';
import * as fromInsight from '../../../portfolio-manager/store/selectors/insight.selector';
import * as fromShared from '../../../shared/store/selectors/shared.selector';
import { AddonsDialogComponent } from '../../../taxation/components/dialogs/addons-dialog/addons-dialog.component';
import { UpgradeFishingDialogComponent } from '../../../taxation/components/dialogs/upgrade-fishing-dialog/upgrade-fishing-dialog.component';
import { Account } from '../../../taxation/models/account.model';
import { PaymentEstimateV3 } from '../../../taxation/models/payment.model';
import { SubscriptionNextPayment } from '../../../taxation/models/subscription.model';
import { User, UserAddon, UserPlan } from '../../../taxation/models/user.model';
import { LegalFormsPageComponent } from '../../../taxation/pages/legal-forms-page/legal-forms-page.component';
import { PlansPageComponent } from '../../../taxation/pages/plans-page/plans-page.component';
import { loadUserAccountsAction } from '../../../taxation/store/actions/account.action';
import {
  loadAvailableAddonsAction,
  loadAvailablePlansAction,
  loadPlansByFiscalYearsAction,
  loadSubscriptionNextPaymentAction,
  loadUpgradeFishingEstimateAction,
} from '../../../taxation/store/actions/payment.action';
import * as fromAccount from '../../../taxation/store/selectors/account.selector';
import * as fromAssessment from '../../../taxation/store/selectors/assessment.selector';
import * as fromPayment from '../../../taxation/store/selectors/payment.selector';
import { Feature, UserFeature } from '../../models/feature.model';
import { Popup, PopupId } from '../../models/popup.model';
import { SidenavComponentType } from '../../models/sidenav.model';
import { UserPreferences } from '../../models/user-preferences.model';
import { FeatureService } from '../../services/feature.service';
import { SidenavService } from '../../services/sidenav.service';
import { UtilsService } from '../../services/utils.service';
import {
  acknowledgePopupAction,
  loadPopupsToDisplayAction,
  loadUserFeaturesAction,
  togglePlansPageAction,
  trackEventAction,
} from '../../store/actions/shared.action';
import { BetaProgramDialogComponent } from '../dialogs/beta-program-dialog/beta-program-dialog.component';
import { DownloadAppDialogComponent } from '../dialogs/download-app-dialog/download-app-dialog.component';
import { NewFeaturesDialogComponent } from '../dialogs/new-features-dialog/new-features-dialog.component';
import { ReferralDialogComponent } from '../dialogs/referral-dialog/referral-dialog.component';
import { SidenavComponent } from '../sidenav/sidenav.component';
import { TaxSeasonEndDialogComponent } from '../../../taxation/components/dialogs/tax-season-end-dialog/tax-season-end-dialog.component';

const LOAD_POPUPS_TO_DISPLAY_DELAY = 15000;

@Component({
  selector: `app-layout`,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatSidenavModule,
    MatButtonModule,
    MatMenuModule,
    MatListModule,
    MatDividerModule,
    SidenavComponent,
    LegalFormsPageComponent,
    TokenSwapperComponent,
    PlansPageComponent,
  ],
  templateUrl: `./layout.component.html`,
  styleUrls: [`./layout.component.scss`],
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(`rightSidenav`) rightSidenav: MatSidenav;

  accounts: Map<string, Account>;

  stablecoins: string[];
  fiats: string[];
  userPreferences: UserPreferences;
  user: User;

  marketFeed: MarketFeed;
  tickerFeed: TickerFeed;

  hasPaid = false;

  upgradeFishingEstimate: PaymentEstimateV3;

  popupsToDisplay: Popup[];
  userFeatures: UserFeature[];
  showPopup = true;

  showPlansPage: boolean;
  plansPagePopupIndex: number;
  plansPagePopupId: PopupId;
  plansPageVoucher: string;

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly paymentStore$: Store<fromPayment.State>,
    private readonly authStore$: Store<fromAuth.State>,
    private readonly sharedStore$: Store<fromShared.State>,
    private readonly assessmentStore$: Store<fromAssessment.State>,
    private readonly insightStore$: Store<fromInsight.State>,
    private readonly accountStore$: Store<fromAccount.State>,
    private readonly sidenavService: SidenavService,
    private readonly utilsService: UtilsService,
    private readonly featureService: FeatureService,
  ) {}

  ngOnInit(): void {
    this.accountStore$.dispatch(loadUserAccountsAction());
    this.paymentStore$.dispatch(loadSubscriptionNextPaymentAction());
    this.paymentStore$.dispatch(loadAvailablePlansAction());
    this.paymentStore$.dispatch(loadAvailableAddonsAction());
    this.paymentStore$.dispatch(loadPlansByFiscalYearsAction());
    this.sharedStore$.dispatch(loadUserFeaturesAction());

    this.loadPopupsToDisplay();

    this.assessmentStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromAssessment.selectCurrentFiscalYear),
        map((fiscalYear: number) => {
          this.paymentStore$.dispatch(
            loadUpgradeFishingEstimateAction({
              fiscalYear,
              checkoutType: `PLAN`,
            }),
          );
        }),
      )
      .subscribe();

    combineLatest([
      this.authStore$.pipe(select(fromAuth.selectUser)),
      this.paymentStore$.pipe(select(fromPayment.selectSubscriptionNextPayment)),
      this.sharedStore$.pipe(select(fromShared.selectFeatures(`lab`))),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([user, subscriptionNextPayment, features]: [User, SubscriptionNextPayment, Feature[]]) => {
          if (user && subscriptionNextPayment && features?.length === 1) {
            this.user = user;
            this.hasPaid = user.plan !== `PLN000` && user.plan !== `PLN300`;

            this.paymentStore$.dispatch(
              loadUpgradeFishingEstimateAction({
                fiscalYear: user.associatedFiscalYear,
                checkoutType: `PLAN`,
              }),
            );

            const userPlan = (subscriptionNextPayment.plans.find(
              (sub: UserPlan | UserAddon) => sub !== `AOCARE001` && sub !== `AOCARE002`,
            ) || `PLN000`) as UserPlan;
            const betaProgram = localStorage.getItem(`beta-program`);
            const openDialog =
              this.featureService.isRangedFeatureEnabled(features[0]) &&
              betaProgram === null &&
              user.fiscalResidency === `FR` &&
              userPlan !== `PLN000`;

            if (openDialog) {
              this.openBetaProgramDialog();
            }
          }
        }),
      )
      .subscribe();

    combineLatest([
      this.sharedStore$.select(fromShared.selectStableCoins),
      this.sharedStore$.select(fromShared.selectFiats),
      this.sharedStore$.select(fromShared.selectUserPreferences),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([stablecoins, fiats, userPreferences]) => {
          this.stablecoins = stablecoins;
          this.fiats = fiats;
          this.userPreferences = userPreferences;
        }),
      )
      .subscribe();

    combineLatest([
      this.insightStore$.select(fromInsight.selectMarketFeed),
      this.insightStore$.select(fromInsight.selectTickerFeed),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([marketFeed, tickerFeed]: [MarketFeed, TickerFeed]) => {
          this.marketFeed = marketFeed;
          this.tickerFeed = tickerFeed;
        }),
      )
      .subscribe();

    this.sharedStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromShared.selectAllAccounts),
        map((accounts: Map<string, Account>) => (this.accounts = accounts)),
      )
      .subscribe();

    this.paymentStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromPayment.selectUpgradeFishingEstimate),
        map((upgradeFishingEstimate: PaymentEstimateV3) => {
          this.upgradeFishingEstimate = upgradeFishingEstimate;
        }),
      )
      .subscribe();

    combineLatest([
      this.sharedStore$.select(fromShared.selectUserFeatures),
      this.sharedStore$.select(fromShared.selectPopupsToDisplay),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([userFeatures, popupsToDisplay]) => {
          if (this.showPopup) {
            this.userFeatures = userFeatures;
            this.popupsToDisplay = popupsToDisplay;

            if (this.userFeatures && this.popupsToDisplay?.length > 0) {
              this.showPopup = false;
              this.openPopup(this.popupsToDisplay[0], 0);
            }
          }
        }),
      )
      .subscribe();

    this.sharedStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromShared.selectShowPlansPage),
        map((showPlansPage: boolean) => {
          this.showPlansPage = showPlansPage;
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.sidenavService.setSidenav(this.rightSidenav);
    this.sidenavService.setSidenavComponentType(`LegalForm`);
  }

  getSidenavComponentType(): SidenavComponentType {
    return this.sidenavService.getSidenavComponentType();
  }

  closeSidenav(): void {
    this.sidenavService.close();
  }

  openBetaProgramDialog(): void {
    setTimeout(() => {
      const dialogRef = this.utilsService.openDialog(BetaProgramDialogComponent, `587px`, `auto`);

      dialogRef
        .afterClosed()
        .pipe(
          takeUntil(this.destroy$),
          map((res: boolean) => {
            if (!res) {
              localStorage.setItem(`beta-program`, `false`);
            }
          }),
        )
        .subscribe();
    }, 10000);
  }

  upgradeFishing(): void {
    this.utilsService.openDialog(UpgradeFishingDialogComponent, `400px`, `auto`, {
      name: `access-to-ph3`,
      checkoutType: `PLAN`,
      title: `OPTIMIZE_PASSIVE_RETURNS`,
      offer: `PLN500`,
      fiscalYear: this.upgradeFishingEstimate.fiscalYear,
      updateTitle: false,
    });
  }

  openEarlyAccessFeatureDialog(): MatDialogRef<any> {
    return this.utilsService.openDialog(EarlyAccessFeatureDialogComponent, `587px`, `auto`, {
      disableClose: true,
    });
  }

  openNewFeaturesDialog(): MatDialogRef<any> {
    return this.utilsService.openDialog(NewFeaturesDialogComponent, `534px`, `auto`, {
      disableClose: true,
    });
  }

  openAddonsDialog(): MatDialogRef<any> {
    return this.utilsService.openDialog(AddonsDialogComponent, `837px`, `auto`, {
      disableClose: false,
    });
  }

  openReferralDialog(): MatDialogRef<any> {
    return this.utilsService.openDialog(ReferralDialogComponent, `512px`, `auto`, {
      disableClose: false,
    });
  }

  openDownloadAppDialog(): MatDialogRef<any> {
    return this.utilsService.openDialog(DownloadAppDialogComponent, `471px`, `auto`, {
      userPreferences: this.userPreferences,
    });
  }

  openTaxSeasonEndDialog(popupId: PopupId): MatDialogRef<any> {
    return this.utilsService.openDialog(TaxSeasonEndDialogComponent, `506px`, `629px`, {
      popupId,
    });
  }

  openPopup(popup: Popup, index: number): void {
    if (popup.platform === `DESKTOP` || popup.platform === `BOTH`) {
      let dialogRef: MatDialogRef<any>;

      switch (popup.id) {
        case `FTR-STAKING-V1`:
          dialogRef = this.openEarlyAccessFeatureDialog();
          break;
        case `FTR-BALANCE-V2`:
          dialogRef = this.openNewFeaturesDialog();
          break;
        case `MKT-ADDONS-V1`:
          dialogRef = this.openAddonsDialog();
          break;
        case `REF-FRIEND-INVITE-2025`:
          dialogRef = this.openReferralDialog();
          break;
        case `MKT-CAMPAIGN-2025-APRIL`:
          this.plansPageVoucher = `APRIL25`;
          this.redirectToPlansPage(popup.id, index);
          break;
        case `MKT-WELCOMEBACK-IN-2024`:
          this.plansPageVoucher = `WELCOMEBACK25`;
          this.redirectToPlansPage(popup.id, index);
          break;
        case `MKT-MOBILEAPP-V1`:
          dialogRef = this.openDownloadAppDialog();
          break;
        case `MKT-ENDZONE-2025-BE`:
        case `MKT-ENDZONE-2025-ES`:
        case `MKT-ENDZONE1-2025-FR`:
        case `MKT-ENDZONE2-2025-FR`:
        case `MKT-ENDZONE3-2025-FR`:
          dialogRef = this.openTaxSeasonEndDialog(popup.id);
          break;
      }

      dialogRef
        ?.afterClosed()
        .pipe(
          takeUntil(this.destroy$),
          tap(() => {
            this.sharedStore$.dispatch(
              acknowledgePopupAction({
                popupId: popup.id,
                acknowledgedFrom: `DESKTOP`,
              }),
            );

            const nextIndex = index + 1;

            if (nextIndex < this.popupsToDisplay.length) {
              this.openPopup(this.popupsToDisplay[nextIndex], nextIndex);
            }
          }),
        )
        .subscribe();
    } else {
      this.sharedStore$.dispatch(
        acknowledgePopupAction({
          popupId: popup.id,
          acknowledgedFrom: `DESKTOP`,
        }),
      );

      const nextIndex = index + 1;

      if (nextIndex < this.popupsToDisplay.length) {
        this.openPopup(this.popupsToDisplay[nextIndex], nextIndex);
      }
    }
  }

  redirectToPlansPage(popupId: PopupId, index: number): void {
    this.plansPagePopupId = popupId;
    this.plansPagePopupIndex = index;

    let upgradeName: string;

    switch (this.plansPagePopupId) {
      case `MKT-WELCOMEBACK-IN-2024`:
        upgradeName = `mkt_welcomeback_in_2024`;
        break;
      case `MKT-CAMPAIGN-2025-APRIL`:
        upgradeName = `mkt_campaign_2025_april`;
        break;
    }

    this.sharedStore$.dispatch(
      trackEventAction({
        event: `upgrade_fishing`,
        data: {
          upgrade_name: upgradeName,
        },
      }),
    );

    this.sharedStore$.dispatch(togglePlansPageAction());
  }

  closePlansPage(): void {
    this.sharedStore$.dispatch(togglePlansPageAction());

    if (this.plansPagePopupId) {
      this.sharedStore$.dispatch(
        acknowledgePopupAction({
          popupId: this.plansPagePopupId,
          acknowledgedFrom: `DESKTOP`,
        }),
      );

      const nextIndex = this.plansPagePopupIndex + 1;

      if (nextIndex < this.popupsToDisplay.length) {
        this.openPopup(this.popupsToDisplay[nextIndex], nextIndex);
      }
    }
  }

  loadPopupsToDisplay(): void {
    setTimeout(() => {
      this.sharedStore$.dispatch(loadPopupsToDisplayAction());
    }, LOAD_POPUPS_TO_DISPLAY_DELAY);
  }
}
